// 외부 링크 화면 공통 오류 분류 (WP5 4단계 §4.3·§5.1)
// 서버 message 원문은 반환하지 않는다.

export type ShareErrorKind =
  | 'SESSION'
  | 'LINK'
  | 'CLOSED'
  | 'EXPIRED'
  | 'CHANGED'
  | 'ALREADY_RESPONDED'
  | 'ALREADY_RECEIVED'
  | 'VALIDATION'
  | 'PAYLOAD'
  | 'NETWORK'
  | 'SERVER'
  | 'UNKNOWN';

export interface ShareError {
  kind: ShareErrorKind;
  status?: number;
  code?: string;
  field?: string;
  reason?: string;
}

/** 조회(GET) 타임아웃 5초 (D-07 §3.2 #11) */
export const SHARE_LOAD_TIMEOUT_MS = 5000;

const SESSION_CODES = new Set(['TOKEN_EXPIRED', 'TOKEN_REVOKED', 'UNAUTHORIZED', 'FORBIDDEN']);
const LINK_CODES = new Set(['SHARE_NOT_FOUND', 'RESPONSE_NOT_SUPPORTED']);
const SERVER_CODES = new Set(['EXTERNAL_SERVICE_UNAVAILABLE', 'RECEIPT_PLANNER_UNAVAILABLE']);
const CODE_KIND: Record<string, ShareErrorKind> = {
  REQUEST_CLOSED: 'CLOSED',
  ORDER_LINK_EXPIRED: 'EXPIRED',
  ORDER_CONTENT_CHANGED: 'CHANGED',
  // 404 이지만 링크 자체가 없는 것이 아님(B5: 아직 제출된 배송정보 없음) — 404 → LINK 판정보다 먼저 VALIDATION 으로 분류
  ORDER_DELIVERY_NOT_FOUND: 'VALIDATION',
  ALREADY_RESPONDED: 'ALREADY_RESPONDED',
  ORDER_ALREADY_RECEIVED: 'ALREADY_RECEIVED',
  PAYLOAD_TOO_LARGE: 'PAYLOAD',
};

function str(v: unknown): string | undefined {
  return typeof v === 'string' && v !== '' ? v : undefined;
}

interface AxiosLike {
  isAxiosError?: boolean;
  response?: {
    status?: unknown;
    data?: { code?: unknown; data?: { field?: unknown; reason?: unknown } | null } | null;
  };
}

/** axios 오류에서 상태·코드·보조정보를 뽑아 kind 로 분류 */
export function classifyShareError(err: unknown): ShareError {
  if (!err || typeof err !== 'object') return { kind: 'UNKNOWN' };
  const e = err as AxiosLike;

  // 응답이 없으면 NETWORK (타임아웃 ECONNABORTED·ERR_NETWORK 포함). axios 오류가 아닌 예외는 UNKNOWN.
  if (!e.response) {
    return e.isAxiosError ? { kind: 'NETWORK' } : { kind: 'UNKNOWN' };
  }

  const status = typeof e.response.status === 'number' ? e.response.status : undefined;
  const body = e.response.data && typeof e.response.data === 'object' ? e.response.data : undefined;
  const code = str(body?.code);
  const extra = body?.data && typeof body.data === 'object' ? body.data : undefined;
  const field = str(extra?.field);
  const reason = str(extra?.reason);
  const base: ShareError = { kind: 'UNKNOWN', status, code };
  if (field) base.field = field;
  if (reason) base.reason = reason;
  const done = (kind: ShareErrorKind): ShareError => ({ ...base, kind });

  if (status === 401 || status === 403 || status === 410) return done('SESSION');
  if (status === 413) return done('PAYLOAD');
  if (status !== undefined && status >= 500) return done('SERVER');

  if (code) {
    if (SESSION_CODES.has(code)) return done('SESSION');
    if (LINK_CODES.has(code)) return done('LINK');
    if (SERVER_CODES.has(code)) return done('SERVER');
    const mapped = CODE_KIND[code];
    if (mapped) return done(mapped);
  }
  if (status === 404) return done('LINK');
  // 표에 없는 4xx 업무 코드(field 유무 무관)는 VALIDATION, code 보존
  if (code && status !== undefined && status >= 400 && status < 500) return done('VALIDATION');
  return done('UNKNOWN');
}

// ── 열람 링크 화면(수신자가 알림톡 링크로 여는 화면) 전용 분류 ──
// 만료·회수·무효·일시 오류 4종 + 접속 시간 초과. 서버 code(SHARE_LINK_*)·data.reason 우선, 없으면 HTTP 상태로 판정한다.
export type ShareLinkViewErrorKind = 'EXPIRED' | 'REVOKED' | 'INVALID' | 'TEMPORARY' | 'SESSION';

export function classifyLinkViewError(err: unknown): ShareLinkViewErrorKind {
  const r = (err as { response?: { status?: number; data?: { code?: string; data?: { reason?: string } } } })?.response;
  const code = r?.data?.code;
  const reason = r?.data?.data?.reason;
  if (code === 'SHARE_LINK_EXPIRED' || reason === 'EXPIRED') return 'EXPIRED';
  if (code === 'SHARE_LINK_REVOKED' || reason === 'REVOKED') return 'REVOKED';
  if (code === 'SHARE_LINK_INVALID' || reason === 'INVALID') return 'INVALID';
  if (code === 'SHARE_LINK_TEMPORARY' || reason === 'TEMPORARY') return 'TEMPORARY';
  const status = r?.status;
  if (status === 410) return 'EXPIRED';
  if (status === 404) return 'INVALID';
  if (status === 401 || status === 403) return 'SESSION';
  return 'TEMPORARY'; // 네트워크 오류·5xx
}
